import 'react-image-crop/dist/ReactCrop.css';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import ReactCrop, { ReactCropProps } from 'react-image-crop';
import type { FileMeta } from '/imports/types';
import { defer, formatBytes, uInt8ToBase64 } from '/imports/utils';
import { strings } from '/imports/strings';
import { initiateAndroidUpload, AndroidClient } from '/imports/android';
import { css } from '/imports/ui/css';
import { Button } from '/imports/ui/Button';
import { LoadingFullScreen } from '/imports/ui/LoadingFullScreen';
import { finishFileUpload, startFileUpload, uploadFileChunk, fileHref, uploadLimitBytes, maxImageUploadBytes, executableUploadRefusal } from '/imports/api/files';
import { FileInputProps } from '/imports/ui/FileInput';
import { createPortal } from 'react-dom';

const ImageInputStyled = styled.div`
  .wrapper { 
    display: flex;
    .meta {
      &.hide {
        display: none;
      }
    }
    .file-widget-thumb {
      margin-right: 1rem;
    }
  }
  input {
    width: 100%;
    border: ${css.inputBorder};
    background: ${css.inputBackgroundColor};
    color: ${css.grey2};
    border-radius: ${css.inputBorderRadius};
    padding: 0.7rem 0.75rem;
    min-height: 3rem;
    box-sizing: border-box;
    font-size: 1rem;
    font-family: ${css.fontFamily};
    &::placeholder {
      text-transform: capitalize;
    }
    &.hide {
      display: none;
    }
  }
  .cropper {
    
  }
  .loading-fullscreen {
    opacity: 0.4;
  }
`;

const CropperStyled = styled.div`
  position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    z-index: 200; /* above Modal overlays (100) — the cropper must win when the input sits in a modal */
    .ReactCrop {
      max-width: 100%;
      max-height: 100%;
      margin: auto;
      overflow: auto;
      &::-webkit-scrollbar {
        width: 0px;
        height: 0px;
      }
      scrollbar-width: 0px;
      -ms-overflow-style: none;
      .ReactCrop__image, .ReactCrop__child-wrapper {
        height: 100% !important;
        width: 100% !important;
        img {
          max-width: 100% !important;
          max-height: 100% !important;
        }
      }
    }
    .buttons {
      position: absolute;
      top: 1rem;
      right: 1rem;
      height: 2rem;
      z-index: -1;
      justify-content: right;
      background: transparent;
      button {
        margin-left: 1rem;
      }
    }
`;

export interface ImageInputProps extends FileInputProps, Omit<ReactCropProps, 'onChange'> {
  className?: string;
  placeholder?: string;
  value?: FileMeta;
  disabled?: boolean;
  onChange?(fileMeta: FileMeta): void;
}

/** File input that lets the user pick/drop an image, crop it, and upload the result. */
export const ImageInput = (props: ImageInputProps) => {
  const { value, id, aspect } = props;
  const maxSize = props.maxSize ?? maxImageUploadBytes;
  const [file, setFile] = useState<File>();
  const [src, setSrc] = useState<string>();
  const [crop, setCrop] = useState<any>({ unit: 'px', x: 0, y: 0, width: 0, height: 0 });
  const [image, setImage] = useState<HTMLImageElement>();
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<any>(undefined);
  /**
   * Handles file-input change events.
   * @param e - input change event
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = Array.from(e.target.files || [])[0];
    file && handleFile(file);
  };
  /**
   * Loads a file as a data URL to feed the cropper.
   * @param file - selected image file
   */
  const handleFile = (file: File) => {
    // Checked on the file the user picked, not on the cropped blob that is actually uploaded: reading a
    // huge source into a data URL is what would hang the browser, and the crop output is always small.
    const limit = uploadLimitBytes(maxSize);
    if (file.size > limit) return toast.error(strings.FileTooLarge(file.name, formatBytes(file.size), formatBytes(limit)));
    // Images only, so a program is never accepted here, whatever the picker was talked into.
    const refusal = executableUploadRefusal(file);
    if (refusal) return toast.error(refusal);
    setFile(file);
    const reader = new FileReader();
    reader.addEventListener('load', () => setSrc(reader?.result as string || undefined));
    reader.readAsDataURL(file);
  };
  /**
   * Initializes the crop rectangle once the image renders.
   * @param event - image load event
   */
  const handleImageLoaded = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const image = event.currentTarget;
    setImage(image);
    const height = 0.75 * image.height;
    const width = 0.75 * image.width;
    // x and y are as required as width and height: without them the crop library writes "undefinedpx" into
    // its mask rect and the browser logs an invalid-length error per attribute.
    if (image.width > image.height) {
      defer(() => setCrop({ unit: 'px', x: 0, y: 0, height, width: aspect ? aspect * height : width }));
    }
    else {
      defer(() => setCrop({ unit: 'px', x: 0, y: 0, width, height: aspect ? aspect * width : height }));
    }
  };
  /** Renders the cropped region to a canvas, uploads it in chunks, and emits the resulting FileMeta. */
  const handleCropComplete = async () => {
    if (!image) return;
    if (!file) return;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    setUploading(true);
    const pixelRatio = window.devicePixelRatio;
    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );
    const outputType = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject();
        },
        outputType,
        1
      );
    });
    const _file = new File([blob], file?.name, { type: outputType });
    let fileMeta: FileMeta | undefined = undefined;
    const { name, type, size } = _file;
    const token = await startFileUpload.call({ name, type, size, maxSize });
    const stream = _file.stream() as any;
    const reader = stream.getReader();
    let read;
    let chunk;
    while ((read = await reader.read())) {
      if (read.value) {
        chunk = uInt8ToBase64(read.value);
        await uploadFileChunk.call({ token, chunk });
      }
      if (read.done) {
        fileMeta = await finishFileUpload.call(token);
        handleCropCancel();
        break;
      }
    }
    fileMeta && props.onChange?.(fileMeta);
  };
  /** Resets cropper state. */
  const handleCropCancel = () => {
    setImage(undefined);
    setFile(undefined);
    setSrc(undefined);
    setUploading(false);
  };
  /**
   * Opens the native picker, routing through the Android bridge when present.
   * @param e - originating click event, if any
   */
  const handleInputClick = (e?: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    if (AndroidClient) initiateAndroidUpload(handleFile);
    else if (!e) fileInput.current?.click();
  };
  return (
    <ImageInputStyled className={props.className}>
      {
        value ?
          <div className="wrapper">
            <Thumb file={value} onClick={() => handleInputClick()} />
          </div>
          :
          null
      }
      {
        src ?
          (
            createPortal(
              <CropperStyled className="cropper">
                <ReactCrop crop={crop}
                  onChange={(crop) => setCrop(crop)}
                  aspect={aspect}
                >
                  <img src={src} onLoad={(e) => handleImageLoaded(e)} />
                </ReactCrop>
                <div className="buttons">
                  <Button className="secondary" onClick={() => handleCropCancel()}>{strings.Cancel()}</Button>
                  <Button onClick={() => handleCropComplete()}>{strings.Done()}</Button>
                </div>
              </CropperStyled>,
              document.body
            )
          )
          :
          null
      }
      {
        uploading ?
          <LoadingFullScreen />
          :
          null
      }
      <input
        id={id}
        ref={fileInput}
        className={value ? 'hide' : ''}
        type="file"
        placeholder={props.placeholder || ''}
        multiple={true}
        accept="image/*"
        disabled={props.disabled}
        onClick={(e) => handleInputClick(e)}
        onChange={(e) => handleChange(e)}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => e.preventDefault()}
        onDrop={async (e) => {
          e.preventDefault();
          if (!fileInput.current) return;
          fileInput.current.files = e.dataTransfer.files;
          const file = Array.from(e.dataTransfer.files)[0];
          file && handleFile(file);
        }}
      />
    </ImageInputStyled>
  );
};

const ThumbStyled = styled.div`
  text-align: center;
  padding-right: 2rem;
  cursor: pointer;
  img, video, iframe {
    max-height: 7rem;
    max-width: 7rem;
  }
`;

interface ThumbProps {
  file: FileMeta;
  onClick?(): void;
}

/** Renders a clickable thumbnail for an uploaded file based on its MIME type. */
const Thumb = (props: ThumbProps) => {
  const file = props.file;
  if (file.type.startsWith('image')) {
    return (
      <ThumbStyled className="file-widget-thumb">
        <a onClick={() => props.onClick?.()}>
          <img src={fileHref(file)} alt={file.name} />
        </a>
      </ThumbStyled>
    );
  }
  if (file.type.startsWith('video')) {
    return (
      <ThumbStyled className="file-widget-thumb">
        <a onClick={() => props.onClick?.()}>
          <video src={fileHref(file)} />
        </a>
      </ThumbStyled>
    );
  }
  if (file.type.startsWith('application/pdf')) {
    return (
      <ThumbStyled className="file-widget-thumb">
        <a onClick={() => props.onClick?.()}>
          <iframe src={fileHref(file)} />
        </a>
      </ThumbStyled>
    );
  }
  return (
    <ThumbStyled className="file-widget-thumb">
      <a href={fileHref(file)}>{file.name}</a>
    </ThumbStyled>
  );
};